import { queryOptions, useMutation } from '@tanstack/react-query';

import { getAccessToken } from '@/lib/tokenStorage';
import type { PaginatedResult, PaginationResult } from '@/services/types';

import type {
  GetDonorScheduleResponse,
  UpsertDonorScheduleRequest,
  UpsertDonorScheduleResponse,
} from './types';

export type {
  GetDonorScheduleResponse,
  GetDonorSchedulesParams,
  UpsertDonorScheduleRequest,
  UpsertDonorScheduleResponse,
} from './types';

function getBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getHeaders(): HeadersInit {
  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function fetchGet<T>(path: string, params?: URLSearchParams): Promise<T> {
  const url = params
    ? `${getBaseUrl()}/api/DonorSchedule/${path}?${params.toString()}`
    : `${getBaseUrl()}/api/DonorSchedule/${path}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as T;
}

async function fetchMutation<T>(
  path: string,
  method: 'POST' | 'PUT',
  body: unknown,
): Promise<T> {
  const url = `${getBaseUrl()}/api/DonorSchedule/${path}`;

  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers: getHeaders(),
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as T;
}

export async function fetchDonorSchedules(
  searchParams: URLSearchParams,
): Promise<PaginationResult<GetDonorScheduleResponse>> {
  return fetchGet<PaginationResult<GetDonorScheduleResponse>>(
    'schedules',
    searchParams,
  );
}

async function upsertDonorSchedule(
  request: UpsertDonorScheduleRequest,
): Promise<UpsertDonorScheduleResponse> {
  return fetchMutation<UpsertDonorScheduleResponse>('upsert', 'POST', request);
}

export const donorSchedulesRestOptions = (params: URLSearchParams) =>
  queryOptions({
    queryKey: ['donor-scheduling', 'schedules', params.toString()],
    async queryFn() {
      return fetchDonorSchedules(params);
    },
  });

export function useUpsertDonorScheduleRestMutation() {
  return useMutation({
    mutationFn: (request: UpsertDonorScheduleRequest) =>
      upsertDonorSchedule(request),
  });
}

export function donorSchedulesQueryOptions(params: URLSearchParams) {
  return queryOptions({
    queryKey: ['donor-scheduling', 'schedules', params.toString()],
    async queryFn() {
      const response = await fetch(
        `${getBaseUrl()}/api/v2/donor-schedule/schedules?${params.toString()}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: getHeaders(),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return (await response.json()) as PaginatedResult<GetDonorScheduleResponse>;
    },
  });
}

export function useReactivateDonorSchedules() {
  return useMutation({
    mutationFn: async (donorScheduleIds: string[]) => {
      const response = await fetch(
        `${getBaseUrl()}/api/v2/donor-schedule/reactivate`,
        {
          method: 'POST',
          credentials: 'include',
          headers: getHeaders(),
          body: JSON.stringify(donorScheduleIds),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return (await response.json()) as string[];
    },
  });
}

export type CreateShipmentGroupResult = {
  code: number;
  success: boolean;
  message?: string | null;
  shipmentGroupId: string;
};

export function useCreateShipmentGroupRestMutation() {
  return useMutation({
    mutationFn: (params: { name: string; estimations: number }) =>
      fetchMutation<CreateShipmentGroupResult>(
        'shipment-group',
        'POST',
        params,
      ),
  });
}

export type CreateShipmentGroupMasterRequest = {
  shipmentGroupId: string;
  name: string;
  contactId: string;
  billIncubator?: boolean;
  billShipping?: boolean;
  shippingMethod?: number;
  shipComment?: string | null;
  callTagComment?: string | null;
  siteId?: string | null;
  callTagContactId?: string | null;
  comment?: string | null;
};

export function useCreateShipmentGroupMasterRestMutation() {
  return useMutation({
    mutationFn: (params: CreateShipmentGroupMasterRequest) =>
      fetchMutation<CreateShipmentGroupResult>(
        'shipment-group-master',
        'POST',
        params,
      ),
  });
}

export type SchedMasterWithAspirationDateDto = {
  aspirationDate?: string | null;
  leftSmallFollicles?: number | null;
  leftMediumFollicles?: number | null;
  leftLargeFollicles?: number | null;
  rightSmallFollicles?: number | null;
  rightMediumFollicles?: number | null;
  rightLargeFollicles?: number | null;
  gradeAbc?: number | null;
  gradeD?: number | null;
  gradeE?: number | null;
  dead?: number | null;
  numberInMat?: number | null;
  ivcNumber?: number | null;
  cleaved?: number | null;
  quality?: number | null;
  low?: number | null;
  totalEmbryos?: number | null;
};

async function fetchPreviousSchedulesV3({
  donorScheduleId,
  donorId,
  ownerId,
}: {
  donorScheduleId: string;
  donorId: string;
  ownerId: string;
}): Promise<SchedMasterWithAspirationDateDto[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('donorScheduleId', donorScheduleId);
  searchParams.set('donorId', donorId);
  searchParams.set('ownerId', ownerId);

  const url = `${getBaseUrl()}/api/v3/donorschedule/previous-schedules?${searchParams.toString()}`;
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as SchedMasterWithAspirationDateDto[];
}

export const previousSchedulesV3Options = ({
  donorScheduleId,
  donorId,
  ownerId,
}: {
  donorScheduleId: string;
  donorId: string;
  ownerId: string;
}) =>
  queryOptions({
    queryKey: [
      'previousSchedules',
      'v3',
      'list',
      { donorScheduleId, donorId, ownerId },
    ],
    async queryFn() {
      return fetchPreviousSchedulesV3({ donorScheduleId, donorId, ownerId });
    },
    retry: false,
    staleTime: Infinity,
  });
